import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, Save, X, Globe, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { destinationLanguagesAPI } from '../../services/api';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';

const SUPPORTED_LANGUAGES = [
  { code: 'zh-CN', name: 'Chinese (Mandarin Simplified)', flag: '🇨🇳' },
  { code: 'zh-HK', name: 'Chinese (Cantonese / HK)', flag: '🇭🇰' },
  { code: 'zh-TW', name: 'Chinese (Traditional / TW)', flag: '🇹🇼' },
  { code: 'ja-JP', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko-KR', name: 'Korean', flag: '🇰🇷' },
  { code: 'hi-IN', name: 'Hindi', flag: '🇮🇳' },
  { code: 'ta-IN', name: 'Tamil', flag: '🇮🇳' },
  { code: 'ur-PK', name: 'Urdu', flag: '🇵🇰' },
  { code: 'bn-BD', name: 'Bengali', flag: '🇧🇩' },
  { code: 'ms-MY', name: 'Malay / Indonesian', flag: '🇲🇾' },
  { code: 'th-TH', name: 'Thai', flag: '🇹🇭' },
  { code: 'ar-AE', name: 'Arabic (Gulf)', flag: '🇦🇪' },
  { code: 'ar-SA', name: 'Arabic (Saudi)', flag: '🇸🇦' },
  { code: 'tr-TR', name: 'Turkish', flag: '🇹🇷' },
  { code: 'ru-RU', name: 'Russian', flag: '🇷🇺' },
  { code: 'fr-FR', name: 'French', flag: '🇫🇷' },
  { code: 'de-DE', name: 'German', flag: '🇩🇪' },
  { code: 'es-ES', name: 'Spanish', flag: '🇪🇸' },
  { code: 'it-IT', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt-PT', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', flag: '🇧🇷' },
  { code: 'nl-NL', name: 'Dutch', flag: '🇳🇱' },
];

const EMPTY_FORM = { type: 'iata', code: '', language_code: 'zh-CN', language_name: 'Chinese (Mandarin Simplified)', notes: '' };

function LookupPanel() {
  const [iata, setIata] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function lookup() {
    if (!iata.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await destinationLanguagesAPI.lookup(iata.trim().toUpperCase(), null);
      setResult(res.data.data);
    } catch {
      toast.error('Lookup failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-3 text-blue-800 font-semibold text-sm">
        <Info size={15} /> Test Language Lookup
      </div>
      <div className="flex items-center gap-2">
        <input
          className="border border-blue-300 rounded-lg px-3 py-1.5 text-sm font-mono w-24 uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="NRT"
          maxLength={6}
          value={iata}
          onChange={e => setIata(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && lookup()}
        />
        <button
          onClick={lookup}
          disabled={loading}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Looking up…' : 'Lookup'}
        </button>
        {result && (
          <span className="text-sm text-blue-800 ml-2">
            {result.flag} <strong>{result.language_name}</strong> ({result.language_code})
            <span className="ml-2 text-xs text-blue-600 bg-blue-100 rounded px-1.5 py-0.5">{result.source}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function OverrideRow({ row, onEdit, onDelete }) {
  const lang = SUPPORTED_LANGUAGES.find(l => l.code === row.language_code);
  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3">
        <StatusBadge label={row.type.toUpperCase()} tone={row.type === 'iata' ? 'purple' : 'teal'} />
      </td>
      <td className="px-4 py-3 font-mono font-bold text-gray-800">{row.code}</td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {lang?.flag || ''} {row.language_name}
        <span className="ml-2 text-xs text-gray-400 font-mono">{row.language_code}</span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate">{row.notes || '—'}</td>
      <td className="px-4 py-3 text-xs text-gray-400">{row.creator?.name || '—'}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => onEdit(row)} className="p-1.5 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors text-gray-400">
            <Edit2 size={14} />
          </button>
          <button onClick={() => onDelete(row)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors text-gray-400">
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function OverrideForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);

  function handleLangChange(code) {
    const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
    setForm(f => ({ ...f, language_code: code, language_name: lang?.name || code }));
  }

  return (
    <Card className="mb-4">
      <div className="text-sm font-semibold text-gray-700 mb-4">{initial ? 'Edit Override' : 'New Override'}</div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={form.type}
            onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
            disabled={!!initial}
          >
            <option value="iata">IATA Airport Code</option>
            <option value="country">Country Code (ISO)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            {form.type === 'iata' ? 'IATA Code (e.g. NRT)' : 'Country Code (e.g. JP)'}
          </label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={form.type === 'iata' ? 4 : 3}
            value={form.code}
            onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
            disabled={!!initial}
            placeholder={form.type === 'iata' ? 'NRT' : 'JP'}
          />
        </div>
      </div>
      <div className="mb-3">
        <label className="block text-xs text-gray-500 mb-1">Secondary Language</label>
        <select
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={form.language_code}
          onChange={e => handleLangChange(e.target.value)}
        >
          {SUPPORTED_LANGUAGES.map(l => (
            <option key={l.code} value={l.code}>{l.flag} {l.name} ({l.code})</option>
          ))}
        </select>
      </div>
      <div className="mb-4">
        <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
        <input
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          placeholder="e.g. Operator preference, large passenger demographic…"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button variant="primary" icon={Save} loading={saving} disabled={!form.code} onClick={() => onSave(form)}>
          Save
        </Button>
        <Button variant="ghost" icon={X} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

export default function AdminDestinationLanguagesPage() {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await destinationLanguagesAPI.getOverrides();
      setOverrides(res.data.data);
    } catch {
      toast.error('Failed to load overrides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(form) {
    setSaving(true);
    try {
      if (editRow) {
        await destinationLanguagesAPI.updateOverride(editRow.id, form);
        toast.success('Override updated');
      } else {
        await destinationLanguagesAPI.createOverride(form);
        toast.success('Override created');
      }
      setShowForm(false);
      setEditRow(null);
      load();
    } catch {
      // api.js global error handler toasts
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (!confirm(`Remove override for ${row.type.toUpperCase()} "${row.code}"?`)) return;
    try {
      await destinationLanguagesAPI.deleteOverride(row.id);
      toast.success('Override removed');
      load();
    } catch { /* global handler */ }
  }

  function handleEdit(row) {
    setEditRow(row);
    setShowForm(true);
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader
        icon={Globe}
        title="Destination Language Mapping"
        subtitle="Override the automatic language detection for specific airport or country codes. The system uses built-in maps for ~150 airports; add overrides here for custom rules."
        actions={!showForm && (
          <Button variant="primary" icon={Plus} onClick={() => { setEditRow(null); setShowForm(true); }}>
            Add Override
          </Button>
        )}
      />

      {/* Lookup tester */}
      <LookupPanel />

      {/* Form */}
      {showForm && (
        <OverrideForm
          initial={editRow}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditRow(null); }}
          saving={saving}
        />
      )}

      {/* Info banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-800 flex items-start gap-2">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>
          Overrides take priority over the built-in map. When a flight syncs in, the system checks:
          (1) IATA override → (2) Country override → (3) Built-in IATA map → (4) Built-in country map → (5) Default language.
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : overrides.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          No custom overrides — built-in maps are active for all destinations.
        </div>
      ) : (
        <Card padded={false} className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Language</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Notes</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Added By</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {overrides.map(row => (
                <OverrideRow key={row.id} row={row} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
